import {
  Menu,
  X,
  Home,
  Mountain,
  BedDouble,
  Sparkles,
  Image as ImageIcon,
  Compass,
  BookOpen,
  Star,
  MapPin,
  Mail,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../context";
import { useThemeColor } from "../../hooks";
import { SECTION_IDS } from "../../data";
import SettingsMenu from "../navigation/SettingsMenu";
import { localizePath } from "../../lib/localeRoutes";

// Renders fixed site navigation and synchronizes route, section highlight, and theme color.
export default function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  // True on pages that aren't the one-page homepage (e.g. /guides, a guide
  // article) — they have none of SECTION_IDS in their DOM, so the scroll-spy
  // below has nothing to track there.
  const [isStandalonePage, setIsStandalonePage] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  // Sync theme-color meta tag with header's background
  useThemeColor(activeSection, isDarkMode);

  // Re-check on every navigation, not just once on mount — Header stays
  // mounted across client-side route changes, so without this a standalone
  // page could keep showing whatever color the previous page's scroll
  // position left behind until the next scroll event happens to fire.
  useEffect(() => {
    const hasSections = SECTION_IDS.some((id) => document.getElementById(id));
    setIsStandalonePage(!hasSections);
    if (!hasSections) setActiveSection("home");
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      // Standalone pages always use the "home" header style (see effect
      // above) — nothing to track here.
      if (isStandalonePage) return;

      const sections = SECTION_IDS;
      const scrollPosition = window.scrollY + 100;

      // Check if we're near the bottom of the page
      const isNearBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 50;

      if (isNearBottom) {
        setActiveSection("contact");
        return;
      }

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isStandalonePage]);

  // Keep the page in place while the mobile navigation is open.
  useEffect(() => {
    if (!isMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMenuOpen]);

  // Navigates to the section route and closes mobile menu state.
  const scrollToSection = (id: string) => {
    navigate(navHref(id));
    setIsMenuOpen(false);
  };

  // Resolves a nav item id to its localized URL. Used both by scrollToSection
  // (the logo/home button) and as the real `href` on the nav Links below —
  // those used to be plain <button onClick> elements with no href at all, so
  // crawlers had no anchor to follow to /guides or any other section route.
  const navHref = (id: string) => {
    const path = id === "home" ? "/" : `/${id}`;
    // Stay within the active language so navigation never drops the visitor
    // back onto the English URLs.
    return localizePath(path, i18n.language);
  };

  // Returns adaptive nav text color for readability against dynamic backgrounds.
  const getTextColor = () => {
    if (
      activeSection === "contact" ||
      activeSection === "findus" ||
      activeSection === "nearby" ||
      activeSection === "reviews"
    )
      return "text-white";
    return scrolled
      ? "text-gray-800 dark:text-gray-100"
      : "text-gray-700 dark:text-gray-200";
  };

  // Resolves localized label for a navigation item key.
  const formatNavText = (text: string) => {
    return t(`header.nav.${text}`);
  };

  const navItems = [
    { id: "home", icon: Home },
    { id: "bhalil", icon: Mountain },
    { id: "rooms", icon: BedDouble },
    { id: "services", icon: Sparkles },
    { id: "gallery", icon: ImageIcon },
    { id: "nearby", icon: Compass },
    { id: "guides", icon: BookOpen },
    { id: "reviews", icon: Star },
    { id: "findus", icon: MapPin },
    { id: "contact", icon: Mail },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 w-full z-50 transition-all duration-500">
      {isMenuOpen && (
        <button
          type="button"
          aria-label={t("header.closeMobileMenu", "Close mobile menu")}
          onClick={() => setIsMenuOpen(false)}
          className="fixed inset-0 z-0 bg-black/20 backdrop-blur-sm md:hidden"
        />
      )}

      <div
        className={`relative z-10 max-w-7xl mx-3 xl:mx-auto my-2 backdrop-blur-2xl transition-all duration-700 rounded-xl ${
          scrolled ? "shadow-lg" : "shadow-md"
        }`}
        style={{
          background: isDarkMode
            ? `linear-gradient(135deg, ${
                activeSection === "home"
                  ? "rgba(26, 15, 10, 0.95)"
                  : activeSection === "bhalil"
                  ? "rgba(35, 20, 14, 0.95)"
                  : activeSection === "rooms"
                  ? "rgba(45, 25, 17, 0.95)"
                  : activeSection === "services"
                  ? "rgba(55, 30, 20, 0.95)"
                  : activeSection === "activities"
                  ? "rgba(60, 35, 22, 0.95)"
                  : activeSection === "gallery"
                  ? "rgba(65, 38, 24, 0.95)"
                  : activeSection === "nearby"
                  ? "rgba(70, 40, 26, 0.95)"
                  : activeSection === "reviews"
                  ? "rgba(72, 41, 27, 0.95)"
                  : activeSection === "findus"
                  ? "rgba(75, 42, 28, 0.95)"
                  : "rgba(80, 45, 30, 0.95)"
              }, ${
                activeSection === "home"
                  ? "rgba(45, 24, 16, 0.90)"
                  : activeSection === "bhalil"
                  ? "rgba(50, 28, 18, 0.90)"
                  : activeSection === "rooms"
                  ? "rgba(55, 32, 20, 0.90)"
                  : activeSection === "services"
                  ? "rgba(60, 35, 23, 0.90)"
                  : activeSection === "activities"
                  ? "rgba(65, 38, 25, 0.90)"
                  : activeSection === "gallery"
                  ? "rgba(70, 42, 27, 0.90)"
                  : activeSection === "nearby"
                  ? "rgba(75, 45, 30, 0.90)"
                  : activeSection === "reviews"
                  ? "rgba(77, 46, 31, 0.90)"
                  : activeSection === "findus"
                  ? "rgba(80, 48, 32, 0.90)"
                  : "rgba(85, 50, 34, 0.90)"
              })`
            : `linear-gradient(135deg, ${
                activeSection === "home"
                  ? "rgba(245, 240, 230, 0.85)"
                  : activeSection === "bhalil"
                  ? "rgba(240, 235, 220, 0.85)"
                  : activeSection === "rooms"
                  ? "rgba(235, 224, 200, 0.85)"
                  : activeSection === "services"
                  ? "rgba(224, 212, 184, 0.85)"
                  : activeSection === "activities"
                  ? "rgba(210, 184, 136, 0.85)"
                  : activeSection === "gallery"
                  ? "rgba(196, 160, 104, 0.85)"
                  : activeSection === "nearby"
                  ? "rgba(180, 142, 88, 0.85)"
                  : activeSection === "reviews"
                  ? "rgba(172, 136, 84, 0.85)"
                  : activeSection === "findus"
                  ? "rgba(165, 124, 72, 0.90)"
                  : "rgba(151, 110, 56, 0.85)"
              }, ${
                activeSection === "home"
                  ? "rgba(255, 255, 255, 0.75)"
                  : activeSection === "bhalil"
                  ? "rgba(245, 240, 230, 0.70)"
                  : activeSection === "rooms"
                  ? "rgba(224, 208, 168, 0.70)"
                  : activeSection === "services"
                  ? "rgba(210, 180, 150, 0.70)"
                  : activeSection === "activities"
                  ? "rgba(203, 172, 120, 0.70)"
                  : activeSection === "gallery"
                  ? "rgba(184, 144, 88, 0.70)"
                  : activeSection === "nearby"
                  ? "rgba(169, 130, 76, 0.70)"
                  : activeSection === "reviews"
                  ? "rgba(161, 124, 72, 0.70)"
                  : activeSection === "findus"
                  ? "rgba(154, 112, 64, 0.75)"
                  : "rgba(139, 90, 43, 0.75)"
              })`,
          backdropFilter: "blur(30px) saturate(180%)",
          WebkitBackdropFilter: "blur(30px) saturate(180%)",
          borderBottom: isDarkMode
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : activeSection === "contact" ||
              activeSection === "findus" ||
              activeSection === "nearby" ||
              activeSection === "reviews"
            ? "1px solid rgba(255, 255, 255, 0.2)"
            : "1px solid rgba(151, 110, 56, 0.1)",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent pointer-events-none rounded-xl"></div>

        <div className="relative max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center py-2 sm:py-2.5">
            <button
              onClick={() => scrollToSection("home")}
              className="flex items-center gap-2 sm:gap-3 group min-w-0"
            >
              <img
                src="/logo.webp"
                alt="Dar Zaytoun Logo"
                className="w-14 h-14 sm:w-16 sm:h-16 object-contain flex-shrink-0 transition-transform group-hover:scale-110 drop-shadow-lg"
              />
              <div className="hidden xs:block min-w-0 text-center">
                <div
                  className={`text-base sm:text-2xl font-serif font-bold ${getTextColor()} truncate transition-colors duration-500 drop-shadow-sm text-center`}
                  aria-label="Dar Zaytoun"
                >
                  {t("header.siteName")}
                </div>
                <p
                  className={`text-xs ${
                    activeSection === "contact" ||
                    activeSection === "findus" ||
                    activeSection === "nearby" ||
                    activeSection === "reviews"
                      ? "text-white/90"
                      : "text-olive-600 dark:text-olive-300"
                  } -mt-0.5 truncate transition-colors duration-500 text-center`}
                >
                  {t("header.location")}
                </p>
              </div>
            </button>

            <nav className="hidden md:flex items-center gap-3.5 lg:gap-5 xl:gap-6">
              {navItems.map(({ id: item }) => (
                <Link
                  key={item}
                  to={navHref(item)}
                  className={`text-sm lg:text-base transition-all capitalize font-medium whitespace-nowrap relative group ${getTextColor()} ${
                    activeSection === item ? "font-bold" : "hover:font-semibold"
                  }`}
                >
                  {formatNavText(item)}
                  <span
                    className={`absolute -bottom-1 left-0 right-0 h-[3px] bg-current transition-all duration-300 ${
                      activeSection === item
                        ? "scale-x-100 opacity-100"
                        : "scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100"
                    }`}
                    style={{
                      borderRadius: "100% 100% 0 0",
                      transformOrigin: "center",
                    }}
                  ></span>
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <SettingsMenu
                isDarkMode={isDarkMode}
                toggleDarkMode={toggleDarkMode}
                activeSection={activeSection}
                isNavMenuOpen={isMenuOpen}
                closeNavMenu={() => setIsMenuOpen(false)}
              />
              <button
                className={`md:hidden p-2 ${getTextColor()} touch-target transition-colors duration-500`}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={t("header.toggleMenu", "Toggle menu")}
              >
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div
          className="relative z-10 md:hidden backdrop-blur-2xl border-t transition-all duration-300 mx-3 rounded-b-2xl shadow-2xl"
          style={{
            background: isDarkMode
              ? "linear-gradient(135deg, rgba(26, 15, 10, 0.96), rgba(45, 24, 16, 0.92))"
              : "linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(248, 244, 236, 0.92))",
            backdropFilter: "blur(25px)",
            WebkitBackdropFilter: "blur(25px)",
            borderColor: isDarkMode
              ? "rgba(255, 255, 255, 0.12)"
              : "rgba(151, 110, 56, 0.15)",
          }}
        >
          {/* Moroccan Ornamental Decoration */}
          <div className="flex items-center justify-center py-3.5 px-4">
            <div
              className={`flex items-center gap-2 ${
                isDarkMode ? "text-caramel-300" : "text-caramel-600"
              }`}
            >
              <span className="text-lg">˙⋆˚‧</span>
              <div className="h-px bg-current w-8"></div>
              <span className="text-xl">୨</span>
              <span
                className={`font-serif font-semibold text-base ${
                  isDarkMode ? "text-gray-100" : "text-gray-800"
                }`}
              >
                {t("header.menu")}
              </span>
              <span className="text-xl">୧</span>
              <div className="h-px bg-current w-8"></div>
              <span className="text-lg">‧˚⋆˙</span>
            </div>
          </div>

          <nav className="grid grid-cols-2 gap-2 px-4 pb-4 safe-bottom">
            {navItems.map(({ id: item, icon: Icon }) => (
              <Link
                key={item}
                to={navHref(item)}
                onClick={() => setIsMenuOpen(false)}
                className={`py-2.5 px-3 text-center font-medium transition-all rounded-xl flex items-center justify-center gap-2 ${
                  activeSection === item
                    ? isDarkMode
                      ? "bg-amber-900/40 text-amber-200 font-bold border border-amber-500/40 shadow-sm ring-1 ring-amber-500/20"
                      : "bg-caramel-100/90 text-caramel-900 font-bold border border-caramel-300/60 shadow-sm ring-1 ring-caramel-400/30"
                    : isDarkMode
                    ? "text-gray-200 hover:bg-white/10"
                    : "text-gray-800 hover:bg-black/5"
                }`}
              >
                <Icon
                  className={`w-4 h-4 flex-shrink-0 transition-colors ${
                    activeSection === item
                      ? isDarkMode
                        ? "text-amber-300"
                        : "text-caramel-700"
                      : isDarkMode
                      ? "text-gray-400"
                      : "text-caramel-600/70"
                  }`}
                />
                <span className="capitalize text-sm font-semibold truncate">{formatNavText(item)}</span>
                {activeSection === item && (
                  <span
                    className={`text-xs flex-shrink-0 ${
                      isDarkMode ? "text-amber-300" : "text-amber-700"
                    }`}
                  >
                    ✦
                  </span>
                )}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
